//-------------------------------------------------------------------------------------------------- Imports
import { Button, Card, Col, Row, Typography } from "antd";
import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { StyleSheet } from "../../StyleSheet";
import SubDefaultLayout from "../../components/SubDefaultLayout";
import Loading from "../../components/Loading";
import { AppDispatch } from "../../stores/store";
import {
  clearAlertConfirmation,
  setAlertConfirmation,
  showAlertConfirmation,
} from "../../stores/slices/alertConfirmationSlice";
import { clearAlert, setAlert, showAlert } from "../../stores/slices/alertSlice";
import { deleteInternetSeguroUser } from "../../methods/Methods";
import {
  getInternetSeguroAccount,
  getInternetSeguroUserDevices,
} from "../../stores/slices/internetSeguroSlice";
import {
  InternetSeguroDevice,
  InternetSeguroUser,
  ResponseInternetSeguroAccountDetail,
} from "../../types/common";
import { getDeviceIcon } from "./deviceIcon";
const { Text } = Typography;

//-------------------------------------------------------------------------------------------------- View
const ParentalControlUser = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { userId } = useParams();
  const account = useSelector(
    (state: any) =>
      state.internetSeguro
        .account as ResponseInternetSeguroAccountDetail | null,
  );
  const accountLoaded = useSelector(
    (state: any) => state.internetSeguro.accountLoaded,
  );
  const devices = useSelector(
    (state: any) =>
      (state.internetSeguro.userDevices[userId as string] ??
        []) as InternetSeguroDevice[],
  );
  const devicesLoaded = useSelector(
    (state: any) => state.internetSeguro.userDevicesLoaded[userId as string],
  );

  const displayUser: InternetSeguroUser | undefined = (
    account?.users ?? []
  ).find((u) => u.userId === userId);

  useEffect(() => {
    if (!accountLoaded) {
      dispatch(getInternetSeguroAccount());
    }
  }, [dispatch, accountLoaded]);

  useEffect(() => {
    if (userId && !devicesLoaded) {
      dispatch(getInternetSeguroUserDevices(userId));
    }
  }, [dispatch, userId, devicesLoaded]);

  const openAlert = (
    type: "SUCCESS" | "ERROR",
    title: string,
    message: string,
    navigateBack: boolean,
  ) => {
    dispatch(
      setAlert({
        show: true,
        type,
        title,
        message,
        onFinalize: () => {
          dispatch(showAlert(false));
          dispatch(clearAlert());
          if (navigateBack) {
            navigate("/parental-control/users");
          }
        },
      }),
    );
  };

  const handleDelete = () => {
    dispatch(showAlertConfirmation(false));
    dispatch(clearAlertConfirmation());
    deleteInternetSeguroUser(userId as string)
      .then((response) => {
        if (response && response.data && response.data.status === "SUCCESS") {
          dispatch(getInternetSeguroAccount());
          openAlert(
            "SUCCESS",
            "¡Estimado usuario!",
            "El usuario ha sido eliminado correctamente.",
            true,
          );
        } else {
          openAlert(
            "ERROR",
            "¡Estimado usuario!",
            "Hubo un error al eliminar el usuario. Intente más tarde.",
            false,
          );
        }
      })
      .catch(() => {
        openAlert(
          "ERROR",
          "¡Estimado usuario!",
          "Hubo un error al eliminar el usuario. Intente más tarde.",
          false,
        );
      });
  };

  const openDeleteConfirm = () => {
    dispatch(
      setAlertConfirmation({
        show: true,
        onPressBtnPrimary: handleDelete,
        onPressBtnSecondary: () => {
          dispatch(showAlertConfirmation(false));
          dispatch(clearAlertConfirmation());
        },
        message: "¿Está seguro que desea eliminar al usuario?",
      }),
    );
  };

  if (!accountLoaded || !devicesLoaded) {
    return <Loading />;
  }

  return (
    <SubDefaultLayout
      title={displayUser?.username ?? "Usuario"}
      needReturn={true}
      navigateTo="/parental-control/users"
    >
      <div style={styles.listContainer}>
        {devices.length === 0 ? (
          <Text style={styles.emptyText}>
            Este usuario no tiene dispositivos
          </Text>
        ) : (
          devices.map((device, index) => {
            const DeviceIcon = getDeviceIcon(device);
            return (
              <Card
                key={device.registrationId || index}
                style={styles.cardStyles}
              >
                <Row align="middle">
                  <Col style={styles.iconContainer}>
                    <DeviceIcon style={styles.deviceIcon} />
                  </Col>
                  <Col flex="auto" style={styles.titleContainer}>
                    <Text style={styles.textName}>{device.name}</Text>
                    <Text style={styles.textSub}>
                      {[device.manufacturer, device.model]
                        .filter(Boolean)
                        .join(" ")}
                    </Text>
                  </Col>
                </Row>
              </Card>
            );
          })
        )}
      </div>
      <div style={styles.buttonContainer}>
        <Button
          size="large"
          style={styles.buttonOrange}
          onClick={() =>
            navigate(`/parental-control/users/${userId}/rules`)
          }
        >
          Reglas de usuario
        </Button>
        <Button
          size="large"
          style={styles.buttonPrimary}
          onClick={() => navigate(`/parental-control/users/${userId}/edit`)}
        >
          Editar usuario
        </Button>
        <Button
          size="large"
          style={styles.buttonDanger}
          onClick={openDeleteConfirm}
        >
          Eliminar usuario
        </Button>
      </div>
    </SubDefaultLayout>
  );
};

//-------------------------------------------------------------------------------------------------- Styles
const styles = StyleSheet.create({
  listContainer: {
    width: "50%",
    margin: "0 auto",
    marginTop: 50,
  },
  cardStyles: {
    marginBottom: 15,
    boxShadow: "0px 0px 8px #00000029",
    borderRadius: 15,
  },
  iconContainer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    paddingRight: 15,
  },
  deviceIcon: {
    fontSize: 28,
    color: "#0055c2",
    marginRight: 10,
  },
  titleContainer: {
    display: "flex",
    flexDirection: "column",
  },
  textName: {
    fontWeight: "bold",
    fontSize: "1.50em",
    color: "#0055c1",
  },
  textSub: {
    fontWeight: 600,
  },
  emptyText: {
    display: "block",
    textAlign: "center",
    paddingVertical: 30,
  },
  buttonContainer: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "center",
    marginTop: 30,
    gap: 15,
    flexWrap: "wrap",
  },
  buttonPrimary: {
    backgroundColor: "#0055c2",
    borderRadius: 30,
    color: "white",
    border: "none",
    padding: "5px 30px",
  },
  buttonOrange: {
    backgroundColor: "#FF7A00",
    borderRadius: 30,
    color: "white",
    border: "none",
    padding: "5px 30px",
  },
  buttonDanger: {
    backgroundColor: "#e00614",
    borderRadius: 30,
    color: "white",
    border: "none",
    padding: "5px 30px",
  },
});

//-------------------------------------------------------------------------------------------------- Exports
export default ParentalControlUser;
